import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export type SortOrder = 'asc' | 'desc';

export interface UseSortParams<T extends string> {
  defaultFields?: T[];
  defaultOrders?: SortOrder[];
}

export const useSort = <T extends string>(params?: UseSortParams<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentFields = useMemo(() => {
    const val = searchParams.get('sortField');
    return val ? (val.split(',') as T[]) : (params?.defaultFields || []);
  }, [searchParams, params?.defaultFields]);

  const currentOrders = useMemo(() => {
    const val = searchParams.get('sortOrder');
    return val ? (val.split(',') as SortOrder[]) : (params?.defaultOrders || []);
  }, [searchParams, params?.defaultOrders]);

  const sorted = useMemo(() => {
    return currentFields.map((field, index) => {
      const order = currentOrders[index] || 'asc';
      return order === 'desc' ? `-${field}` : field;
    });
  }, [currentFields, currentOrders]);

  const handleSortChange = useCallback(
    (field: T) => {
      setSearchParams((prev) => {
        const fieldIndex = currentFields.indexOf(field);

        const newFields = [...currentFields];
        const newOrders = [...currentOrders];

        if (fieldIndex === -1) {
          newFields.push(field);
          newOrders.push('asc');
        } else if (newOrders[fieldIndex] === 'asc') {
          newOrders[fieldIndex] = 'desc';
        } else {
          newFields.splice(fieldIndex, 1);
          newOrders.splice(fieldIndex, 1);
        }

        if (newFields.length > 0) {
          prev.set('sortField', newFields.join(','));
          prev.set('sortOrder', newOrders.join(','));
        } else {
          prev.delete('sortField');
          prev.delete('sortOrder');
        }

        return prev;
      });
    },
    [currentFields, currentOrders, setSearchParams]
  );

  return {
    currentFields,
    currentOrders,
    sorted,
    handleSortChange,
  };
};
